import {
  HelpCircle,
  Mail,
  BookOpen,
  MessageSquare,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const helpItems = [
  {
    title: "Getting Started",
    description:
      "Connect your Google Business Profile and sync your first reviews.",
    icon: BookOpen,
    path: "/help/getting-started",
  },
  {
    title: "Review Responses",
    description: "Learn how AI responses are generated, edited, and approved.",
    icon: MessageSquare,
    path: "/help/review-responses",
  },
  {
    title: "Google Connection",
    description: "Troubleshoot Google OAuth, permissions, and profile access.",
    icon: ShieldCheck,
    path: "/help/google-connection",
  },
];

const faqs = [
  {
    question: "Do responses post automatically?",
    answer:
      "Only if you enable auto-posting. By default, responses can require approval before being posted.",
  },
  {
    question: "Can I edit AI-generated responses?",
    answer:
      "Yes. You can edit any suggested response before approving or posting it.",
  },
  {
    question: "Can I disconnect Google?",
    answer:
      "Yes. You can manage or revoke Google access from Settings at any time.",
  },
  {
    question: "How do I cancel billing?",
    answer:
      "You can manage your subscription from the Billing page or contact support.",
  },
];

export default function Help() {
  return (
    <div className="space-y-6">
      <section className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-2xl p-6 md:p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center">
            <HelpCircle size={24} />
          </div>

          <div>
            <h2 className="text-2xl font-bold">How can we help?</h2>
            <p className="text-indigo-100 mt-1">
              Find answers, setup guidance, and support options.
            </p>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-2xl p-2 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            placeholder="Search help articles..."
            className="flex-1 px-4 py-3 rounded-xl text-slate-900 focus:outline-none"
          />

          <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl text-sm font-medium transition">
            Search
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {helpItems.map((item) => {
          const Icon = item.icon;

          return (
            <Link
              to={item.path}
              key={item.title}
              className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 text-left hover:shadow-md transition"
            >
              <div className="h-11 w-11 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mb-4">
                <Icon size={21} />
              </div>

              <h3 className="font-semibold text-slate-900">{item.title}</h3>

              <p className="text-sm text-slate-500 mt-2">{item.description}</p>
            </Link>
          );
        })}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="divide-y divide-slate-200">
            {faqs.map((faq) => (
              <button
                key={faq.question}
                className="w-full p-5 text-left hover:bg-slate-50 transition flex items-start justify-between gap-4"
              >
                <div>
                  <h3 className="font-medium text-slate-900">{faq.question}</h3>

                  <p className="text-sm text-slate-500 mt-1">{faq.answer}</p>
                </div>

                <ChevronRight
                  size={18}
                  className="text-slate-400 shrink-0 mt-1"
                />
              </button>
            ))}
          </div>
        </div>

        <aside className="space-y-5">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <Mail size={20} className="text-indigo-600" />
              <h2 className="font-semibold text-slate-900">Contact Support</h2>
            </div>

            <p className="text-sm text-slate-500">
              Need help with setup, billing, or your Google connection?
            </p>

            <a
              href="mailto:support@revushield.com"
              className="mt-4 inline-flex w-full justify-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition"
            >
              Email Support
            </a>
          </section>

          <section className="bg-slate-900 text-white rounded-2xl shadow-sm p-5">
            <h2 className="font-semibold">System Status</h2>

            <div className="mt-4 space-y-3 text-sm">
              <StatusRow label="Dashboard" />
              <StatusRow label="Google Sync" />
              <StatusRow label="AI Responses" />
              <StatusRow label="Email Delivery" />
            </div>
          </section>
        </aside>
      </section>
    </div>
  );
}

function StatusRow({ label }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-300">{label}</span>

      <span className="flex items-center gap-2 text-green-300">
        <span className="h-2 w-2 rounded-full bg-green-400" />
        Operational
      </span>
    </div>
  );
}
